import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { getOffsetDate, startOfDay } from '@app/core/util/date_utils';
import { Popover } from 'primeng/popover';
import {
  NotificationAlertBulkActionValue,
  NotificationAlertFilterValue,
  INotificationAlertBulkActionOption,
  INotificationAlertFilterOption,
} from '@app/account/notifications/interfaces/notifications-data.interfaces';
import {
  ChannelNotification,
  NotificationRuleSeverityEnum,
  NotificationSlugEnum,
} from '@app/generated/graphql';
import { NotificationIconConfig } from '@app/account/notifications-and-alerts/notification.icon.config';
import { NotificationPreferencesAnalyticsService } from '@app/account/notification-preferences/services/notification-preferences-analytics.service';
import { NotificationPreferencesConfigService } from '@app/account/notification-preferences/services/notification-preferences-config.service';

@Component({
  selector: 'app-alerts-menu',
  templateUrl: './alerts-menu.component.html',
  standalone: false,
})
export class AlertsMenuComponent {
  // dependencies
  private _router: Router = inject(Router);
  private _notificationIconConfig = inject(NotificationIconConfig);
  private _analyticsService = inject(NotificationPreferencesAnalyticsService);
  private _notificationPreferencesConfig = inject(
    NotificationPreferencesConfigService,
  );

  // inputs
  layout = input<'menu' | 'standalone'>('standalone');
  alerts = input<ChannelNotification[]>([]);
  isLoading = input<boolean>(false);
  loadingMore = input<boolean>(false);
  hasNextPage = input<boolean>(false);
  totalAlertsCount = input<number>(0);
  unreadAlertsCount = input<number>(0);
  filteredAlertsCount = input<number>(0);

  // outputs
  alertsCountChange = output<number>();
  markAllAsRead = output<string[]>();
  archiveAllRead = output<string[]>();
  archiveAll = output<string[]>();
  filterAlerts = output<NotificationAlertFilterValue>();
  loadMore = output<void>();

  markAlertAsRead = output<ChannelNotification>();
  markAlertAsUnread = output<ChannelNotification>();
  archiveAlert = output<ChannelNotification>();
  alertClick = output<ChannelNotification>();

  alertPopover = viewChild<Popover>('alertsPopover');
  alertFilterPopover = viewChild<Popover>('alertFilterPopover');
  alertBulkActionsPopover = viewChild<Popover>('alertBulkActionsPopover');

  // signals
  alertFilterPopoverVisible = signal<boolean>(false);
  alertBulkActionsPopoverVisible = signal<boolean>(false);
  alertsPopoverVisible = signal<boolean>(false);
  drawerVisible = signal<boolean>(false);
  private _alertInboxOpenedAt = signal<Date | null>(null);
  // computed signals
  private _startOfToday = computed(() => startOfDay(new Date()));
  private _startOfYesterday = computed(() => startOfDay(getOffsetDate(1)));

  alertsCount = computed(() => this.alerts()?.length ?? 0);

  alertsTitle = computed(() => {
    return this.selectedFilter()?.value ===
      NotificationAlertFilterValue.Archived
      ? 'Archived Alerts'
      : 'Critical Alerts';
  });

  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.totalAlertsCount() >= 6,
  );

  alertsToday = computed(() => {
    const startOfToday: Date = this._startOfToday();
    return this.alerts()?.filter((alert: ChannelNotification) => {
      return alert?.deliveredAt && new Date(alert.deliveredAt) >= startOfToday;
    });
  });

  alertsYesterday = computed(() => {
    const startOfToday: Date = this._startOfToday();
    const startOfYesterday: Date = this._startOfYesterday();

    return this.alerts()?.filter((alert: ChannelNotification) => {
      if (!alert?.deliveredAt) return false;
      const deliveredAt: Date = new Date(alert.deliveredAt);
      return deliveredAt >= startOfYesterday && deliveredAt < startOfToday;
    });
  });

  alertsOlder = computed(() => {
    const startOfYesterday = this._startOfYesterday();
    return this.alerts()?.filter((alert: ChannelNotification) => {
      return (
        alert?.deliveredAt && new Date(alert.deliveredAt) < startOfYesterday
      );
    });
  });

  showFilterIndicator = computed(() => {
    const value = this.selectedFilter()?.value;
    return (
      value === NotificationAlertFilterValue.UnreadOnly ||
      value === NotificationAlertFilterValue.Archived
    );
  });

  filterOptions: INotificationAlertFilterOption[] = [
    {
      label: 'Unread & read',
      value: NotificationAlertFilterValue.UnreadAndRead,
      iconUrl: 'assets/icons/general/action-list.svg',
    },
    {
      label: 'Unread only',
      value: NotificationAlertFilterValue.UnreadOnly,
      iconUrl: 'assets/icons/general/communication-unread.svg',
    },
    {
      label: 'Archived',
      value: NotificationAlertFilterValue.Archived,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  bulkActionOptions: INotificationAlertBulkActionOption[] = [
    {
      label: 'Mark all as read',
      value: NotificationAlertBulkActionValue.MarkAllRead,
      iconUrl: 'assets/icons/general/system-feedback-success.svg',
    },
    {
      label: 'Archive all',
      value: NotificationAlertBulkActionValue.ArchiveAll,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
    {
      label: 'Archive all read',
      value: NotificationAlertBulkActionValue.ArchiveAllRead,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  NotificationAlertFilterValue = NotificationAlertFilterValue;

  // selections
  selectedFilter = signal<INotificationAlertFilterOption | null>(
    this.filterOptions[0],
  );
  selectedBulkAction = signal<INotificationAlertBulkActionOption | null>(null);

  constructor() {
    effect(() => {
      this.alertsCountChange.emit(this.alertsCount());
    });

    effect(() => {
      if (!this.drawerVisible()) {
        this.selectedFilter.set(this.filterOptions[0]);
        this.filterAlerts.emit(NotificationAlertFilterValue.UnreadAndRead);
      }
    });
  }

  onScroll(event: Event): void {
    const target: HTMLElement | null = event.target as HTMLElement;
    if (!target) return;

    const threshold: number = 100;
    const distanceFromBottom: number =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (
      distanceFromBottom < threshold &&
      this.hasNextPage() &&
      !this.loadingMore()
    ) {
      this.loadMore.emit();
    }
  }

  onStandaloneButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.isSidepanelLayout()) {
      this.drawerVisible.update((visible: boolean) => !visible);
      this.alertPopover()?.hide();
    } else {
      this.alertPopover()?.toggle(event);
      this.drawerVisible.set(false);
    }
  }

  onAlertsPopoverShow(): void {
    this.alertsPopoverVisible.set(true);

    this._alertInboxOpenedAt.set(new Date());
    this._analyticsService.trackAlertCenterOpened({
      unreadCount: this.unreadAlertsCount(),
    });

    this._analyticsService.trackAlertHistoryViewed({
      totalCount: this.totalAlertsCount(),
      unreadCount: this.unreadAlertsCount(),
    });
  }

  onAlertsPopoverHide(): void {
    this.alertsPopoverVisible.set(false);

    // Reset the selected filter and notify orchestrator when the alerts popover is hidden
    this.selectedFilter.set(this.filterOptions[0]);
    this.filterAlerts.emit(NotificationAlertFilterValue.UnreadAndRead);

    this._trackAlertCenterClosed();
  }

  onAlertFilterPopoverShow(): void {
    this.alertFilterPopoverVisible.set(true);
  }

  onAlertFilterPopoverHide(): void {
    this.alertFilterPopoverVisible.set(false);
  }

  onAlertBulkActionsPopoverShow(): void {
    this.alertBulkActionsPopoverVisible.set(true);
  }

  onAlertBulkActionsPopoverHide(): void {
    this.alertBulkActionsPopoverVisible.set(false);
  }

  onFilterButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertBulkActionsPopoverVisible()) {
      this.alertBulkActionsPopover()?.hide();
    }

    this.alertFilterPopover()?.toggle(event);
  }

  onFilterSelectionChange(
    selectedFilter: INotificationAlertFilterOption | null,
  ): void {
    if (!selectedFilter) return;

    this.selectedFilter.set(selectedFilter);

    this.filterAlerts.emit(selectedFilter.value);

    this.alertFilterPopover()?.hide();

    this._analyticsService.trackAlertHistoryFiltered({
      filterValue: selectedFilter.label,
      resultsCount: this.filteredAlertsCount(),
    });
  }

  onBulkActionsButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertFilterPopoverVisible()) {
      this.alertFilterPopover()?.hide();
    }

    this.alertBulkActionsPopover()?.toggle(event);
  }

  onBulkActionSelectionChange(
    selected: INotificationAlertBulkActionOption | null,
  ): void {
    const value: NotificationAlertBulkActionValue | undefined = selected?.value;
    if (!value) return;

    if (!this.alerts()?.length) return;

    switch (value) {
      case NotificationAlertBulkActionValue.MarkAllRead:
        this.onMarkAllAsRead();
        break;
      case NotificationAlertBulkActionValue.ArchiveAllRead:
        this.onArchiveAllRead();
        break;
      case NotificationAlertBulkActionValue.ArchiveAll:
        this.onArchiveAll();
        break;
    }

    this.selectedBulkAction.set(null);
    this.alertBulkActionsPopover()?.hide();
  }

  onMarkAllAsRead(): void {
    const ids: string[] = this.alerts()
      .filter((alert: ChannelNotification) => !alert?.readAt)
      .map((alert: ChannelNotification) => alert.id);

    if (!ids?.length) return;

    this._analyticsService.trackAlertsBatchAction({
      actionType: 'mark_all_read',
      affectedCount: ids.length,
    });

    this.markAllAsRead.emit(ids);
  }

  onArchiveAllRead(): void {
    const ids: string[] = this.alerts()
      .filter(
        (alert: ChannelNotification) => alert?.readAt && !alert?.archivedAt,
      )
      .map((alert: ChannelNotification) => alert.id);

    if (!ids?.length) return;

    this._analyticsService.trackAlertsBatchAction({
      actionType: 'archive_all_read',
      affectedCount: ids.length,
    });

    this.archiveAllRead.emit(ids);
  }

  onArchiveAll(): void {
    const ids: string[] = this.alerts()
      .filter((alert: ChannelNotification) => !alert?.archivedAt)
      .map((alert: ChannelNotification) => alert.id);

    if (!ids?.length) return;

    this._analyticsService.trackAlertsBatchAction({
      actionType: 'archive_all',
      affectedCount: ids.length,
    });

    this.archiveAll.emit(ids);
  }

  openSettings(): void {
    this._router.navigate([
      '/account/notification-preferences/general-preferences',
    ]);

    this._hideView();
  }

  onToggleAlertMarkAsRead(alert: ChannelNotification): void {
    if (!alert) return;

    if (!alert?.readAt) {
      this.markAlertAsRead.emit(alert);
    } else {
      this.markAlertAsUnread.emit(alert);
    }
  }

  onArchiveAlert(alert: ChannelNotification): void {
    if (!alert) return;

    this.archiveAlert.emit(alert);
  }

  onAlertClick(alert: ChannelNotification): void {
    if (!alert) return;

    this._hideView();
    this.alertClick.emit(alert);

    const deliveredAt: Date = new Date(alert.deliveredAt);

    const timeSinceReceivedSeconds: number =
      Math.round((Date.now() - deliveredAt.getTime()) / 1000) || 0;

    this._analyticsService.trackAlertClicked({
      alert: {
        id: alert.id,
        notificationSlug:
          alert.notificationRule?.notificationSlug ??
          NotificationSlugEnum.Example,
        name: alert.notificationRule?.name ?? '',
        description: alert.notificationRule?.description ?? '',
        severity: NotificationRuleSeverityEnum.Alert,
      },
      timeSinceReceivedSeconds,
      source: 'history',
    });
  }

  private _hideView(): void {
    this.alertPopover()?.hide();
    this.alertFilterPopover()?.hide();
    this.alertBulkActionsPopover()?.hide();

    this.drawerVisible.set(false);
  }

  getAlertIcon(alert: ChannelNotification): string {
    return this._notificationIconConfig.getIcon(
      alert.notificationRule.severity,
    );
  }

  /** Delegates to NotificationPreferencesConfigService for dynamic title/body (single source of truth). */
  resolvedChannelNotificationCopy(notification: ChannelNotification): {
    title: string;
    body: string;
  } {
    return this._notificationPreferencesConfig.getResolvedChannelNotificationCopy(
      notification,
    );
  }

  onAlertDrawerShow(): void {
    this._alertInboxOpenedAt.set(new Date());
    this._analyticsService.trackAlertCenterOpened({
      unreadCount: this.unreadAlertsCount(),
    });

    this._analyticsService.trackAlertHistoryViewed({
      totalCount: this.totalAlertsCount(),
      unreadCount: this.unreadAlertsCount(),
    });
  }

  onAlertDrawerHide(): void {
    this._trackAlertCenterClosed();
  }

  private _trackAlertCenterClosed(): void {
    const openedAt: Date | null = this._alertInboxOpenedAt();
    if (!openedAt) return;

    const timeOpenSeconds: number = Math.round(
      (Date.now() - openedAt.getTime()) / 1000,
    );

    this._analyticsService.trackAlertCenterClosed({
      timeOpenSeconds,
    });

    this._alertInboxOpenedAt.set(null);
  }
}
